import * as React from 'react'
import ReactDOM from 'react-dom'
import {createSelector} from 'reselect'

import {later} from '@base/later'
import {splitProps, pickProps, classNames} from '@base/react-utils'
import {addComponentTreeDidMount} from '@base/react-utils/componentTree'

import {calculateStyle, calculateStrategies, areResultsEqual} from './calculate'
import {
  resolvePlacements,
  resolveRectangle,
  resolveRectangles,
} from './resolvers'
import {maximizeSize} from './strategies'
import type {
  CalculateResult,
  PositionOptions,
  AnchorOptions,
  Rectangle,
  Placements,
  ElementOrRectangleOrFn,
} from './types'

import css from './TetherPosition.css'

export const POSITION_PROPS = [
  'alignX',
  'alignY',
  'anchorX',
  'anchorY',
  'anchorPadding',
  'containerPadding',
  'minWidth',
  'minHeight',
  'maxWidth',
  'maxHeight',
  'width',
  'height',
  'allowShift',
  'allowShiftX',
  'allowShiftY',
  'reserveWidth',
  'reserveHeight',
]

export const TETHER_POSITION_PROPS = [
  'placement',
  'target',
  'container',
  'targetContainer',
  'containerPadding',
  ...POSITION_PROPS,
]

export type TetherPositionProps = {
  placement: Placements
  // Target to measure targetRect from
  target?: ElementOrRectangleOrFn
  // Container to measure containerRect & visibleRect from
  container?: ElementOrRectangleOrFn
  // Target container to measure targetVisibleRect from
  targetContainer?: ElementOrRectangleOrFn
  containerPadding?: number | Partial<Rectangle>
  onPosition?: (a: CalculateResult) => void
  children: React.ReactElement<any>
  className?: string
} & PositionOptions &
  AnchorOptions

type State = {
  position: CalculateResult | undefined | null
  preflight: boolean
}

// @ts-ignore
@addComponentTreeDidMount // eslint-disable-line react/no-unsafe
class TetherPosition extends React.Component<TetherPositionProps, State> {
  static displayName = 'TetherPosition'

  static defaultProps = {
    alignX: '50%',
    alignY: '50%',
    anchorX: '50%',
    anchorY: '50%',
    allowShift: '100%',
    anchorPadding: 0,
    container: window,
  }

  node: Element | undefined | null = null

  calculateLater = later(this.calculate.bind(this))
  getStrategies = createSelector(
    (props: TetherPositionProps) => props.placement,
    resolvePlacements
  )

  state: State = {
    position: undefined,
    preflight: false,
  }

  UNSAFE_componentWillMount() {
    // console.info('componentWillMount', this.props)
  }

  componentDidMount() {
    // console.info('componentDidMount', this.props)
    // @ts-ignore
    this.node = ReactDOM.findDOMNode(this)
  }

  // TetherPosition needs up-to-date information about it's parent.
  // As tethers set their position in render(), the position is actually set on DOM only when the whole tree is rendered.
  // As componentDidMount() is called in ascending order, we need to reverse it, so that child tethers start their positioning AFTER any parent.
  //
  // @addComponentTreeDidMount decorator adds handling for that
  componentTreeDidMount() {
    this.startTethering()
  }

  componentWillUnmount() {
    this.node = null
    this.calculateLater.destroy()
  }

  startTethering() {
    // console.info('startTethering', this.props)
    this.calculateLater.now()
  }

  calculate() {
    // node is set when mounted and unset when unmounted...
    if (!this.node) return
    this.doCalculate(this.node)
    // By using non-batched animationFrame, we're sure, that tether positions
    // are updated separately (we do full calculate->setState->render->reconcile)
    // This way child tethers can properly measure their parents
    this.calculateLater.animationFrame()
  }

  doCalculate(element: Element) {
    const {target, container, targetContainer, containerPadding} = this.props

    const rectangles = resolveRectangles({
      element,
      target,
      targetContainer,
      container,
      containerPadding,
    })

    if (!rectangles) {
      // there is nothing to position against
      return
    }
    const strategies = this.getStrategies(this.props)

    let lastPosition = this.state.position
    // In preflight the element will be rendered hidden, with position set to fill
    // the container. This way, it's actual required size can be obtained without
    // overflowing the container or position glitches.
    const preflight = !lastPosition
    const wasPreflight = this.state.preflight

    if (lastPosition && !strategies.includes(lastPosition.strategy)) {
      // in case the list of strategies changes, we should discard the last result
      lastPosition = undefined
    }

    const options = pickProps(this.props, POSITION_PROPS)

    // first, we need the biggest space to safely fill without glitches (like overflow, or autoFocus scrolling)
    const strategyResult = preflight
      ? calculateStrategies({
          ...rectangles,
          ...options,
          // always use a container
          containerRect: rectangles.containerRect || resolveRectangle(window),
          strategies: [maximizeSize],
        })
      : calculateStrategies({
          ...rectangles,
          ...options,
          strategies,
          lastResult: lastPosition,
        })

    const position = {
      ...strategyResult,
      style: calculateStyle(element, strategyResult),
    }

    if (
      position.rating > 0 ||
      !lastPosition ||
      lastPosition.rating > 0 ||
      preflight ||
      wasPreflight
    ) {
      if (!areResultsEqual(this.state.position, position)) {
        this.setState({position, preflight})
        // console.log(`TetherPosition ${preflight ? 'preflight' : 'update'}`, position, this.props)
        if (this.props.onPosition && !preflight) this.props.onPosition(position)
      }
    }
  }

  render() {
    const {position, preflight} = this.state
    // don't render children until we have position calculated
    if (!position || !position.strategy) {
      return <div className={css.emptyContainer} />
    }
    const [childStyle, containerStyle] = splitProps(position.style, [
      'width',
      'height',
      'maxWidth',
      'maxHeight',
      'minWidth',
      'minHeight',
    ])
    let child = React.Children.only(this.props.children)
    child = React.cloneElement(child, {
      style: {
        ...childStyle,
        ...child.props.style,
      },
    })
    return (
      <div
        style={containerStyle}
        className={classNames([
          css.container,
          preflight && css.preflight,
          this.props.className,
        ])}
        {...pickProps(this.props, /^data-/)}
      >
        {child}
      </div>
    )
  }
}

export default TetherPosition
